import React from 'react';
import BreathingFlameMark from '../components/BreathingFlameMark';

/**
 * LoaderPreview — picking the breathing intensity for the AI-generation
 * loader. Uses the official V4.5 PNG at three intensities and three
 * sizes so the founder can eyeball the right setting before we wire it
 * into IgnitingLoader.jsx. Reach it at /loader-preview.
 */
const LoaderPreview = () => {
  const intensities = [
    {
      key: 'subtle',
      title: 'Subtle',
      desc: '4.2s cycle · ±2% scale · brightness only · halo stays mellow',
    },
    {
      key: 'normal',
      title: 'Normal (proposed)',
      desc: '3.4s cycle · ±3.5% scale · brightness + saturation · matched halo',
    },
    {
      key: 'lively',
      title: 'Lively',
      desc: '2.6s cycle · ±6% scale · stronger glow swing',
    },
  ];

  return (
    <div className="min-h-screen bg-cement-1000 text-enf-text-primary px-6 py-12">
      <div className="max-w-5xl mx-auto space-y-12">
        <header className="space-y-2">
          <div className="text-xs uppercase tracking-[0.3em] text-enf-accent-fg">
            Internal · breathing flame preview
          </div>
          <h1 className="font-display text-4xl text-enf-text-primary">Pick a breathing intensity</h1>
          <p className="text-sm text-enf-text-secondary max-w-2xl">
            Same V4.5 mark, three breathing speeds. Tell me <strong>subtle / normal / lively</strong>
            {' '}(or "slower", "less halo", etc.) and I'll wire it into the AI generation loader.
          </p>
        </header>

        {/* Big hero of each intensity */}
        <section className="grid md:grid-cols-3 gap-6">
          {intensities.map((opt) => (
            <div
              key={opt.key}
              className={`rounded-xl border p-5 bg-cement-900 ${
                opt.key === 'normal'
                  ? 'border-enf-accent-fg shadow-[0_0_24px_rgba(220,38,38,0.25)]'
                  : 'border-cement-700'
              }`}
            >
              <div className="text-xs font-display uppercase tracking-widest text-enf-text-secondary mb-1">
                {opt.title}
              </div>
              <div className="text-[11px] text-enf-text-muted mb-4">{opt.desc}</div>
              <div className="bg-enf-surface-mid rounded-md flex items-center justify-center p-6">
                <BreathingFlameMark size={220} intensity={opt.key} />
              </div>
            </div>
          ))}
        </section>

        {/* In-context — exact loader chrome */}
        <section className="space-y-3">
          <h2 className="font-display text-2xl text-enf-text-primary">
            In-context preview — exactly how the AI loader will look
          </h2>
          <div className="bg-cement-900 border border-cement-700 rounded-lg p-10 flex flex-col items-center gap-5">
            <BreathingFlameMark size={140} intensity="normal" />
            <div className="font-display tracking-widest text-enf-text-secondary text-base uppercase enferno-loader-text">
              Igniting your workout...
            </div>
          </div>
        </section>

        {/* Size sanity check — at the typical render sizes */}
        <section className="space-y-3">
          <h2 className="font-display text-2xl text-enf-text-primary">Size sanity check (normal intensity)</h2>
          <div className="bg-cement-900 border border-cement-700 rounded-lg p-8 flex items-end justify-around gap-6">
            {[80, 120, 160, 220].map((s) => (
              <div key={s} className="flex flex-col items-center gap-2">
                <div className="bg-enf-surface-mid rounded-md flex items-center justify-center p-4">
                  <BreathingFlameMark size={s} intensity="normal" />
                </div>
                <div className="text-xs text-enf-text-muted">{s}px</div>
              </div>
            ))}
          </div>
        </section>

        <footer className="text-xs text-enf-text-muted pt-6 border-t border-cement-800">
          Reply with <strong>subtle / normal / lively</strong> — or any tweak (speed, halo strength,
          brightness range). Once you greenlight, I'll swap this into the live loader and the hand-coded
          one goes in the trash.
        </footer>
      </div>
    </div>
  );
};

export default LoaderPreview;
